import React, { useContext, useEffect } from 'react';
import type { FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { LinkButton } from '@atlaskit/button/new';

import {
	AUTOMATION_MANUAL_TRIGGERS,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';

import { useAutomationMenu } from '../common/ConfluenceManualRulesContainer';
import { useAutomationPermissionService } from '../permission-service';

type AutomationModalFooterProps = {
	selectedRuleId: number | undefined;
	onClose: () => void;
	inRovoButton?: boolean;
};

const i18n = defineMessages({
	modalFooterCancelButton: {
		id: 'automation-menu.modal.footer.cancel-button',
		defaultMessage: 'Cancel',
		description: 'The text for a cancel button, which closes the active modal.',
	},
	modalFooterAutomateButton: {
		id: 'automation-menu.modal.footer.automate-button',
		defaultMessage: 'Automate',
		description: 'The text for an automate button, which executes an automation rule.',
	},
	modalFooterCreateAutomationButton: {
		id: 'automation-menu.modal.footer.get-started-button',
		defaultMessage: 'Create automation',
		description:
			'The text for a button that links to the automation rule builder. This button only appears when the modal has no existing automations to display.',
	},
	modalFooterOkButton: {
		id: 'automation-menu.modal.footer.ok-button',
		defaultMessage: 'Ok',
		description:
			'The text for a button that closes the active modal. This button only appears when the modal has no existing automations to display.',
	},
});

export const AutomationModalFooter: FC<AutomationModalFooterProps> = ({
	selectedRuleId,
	onClose,
	inRovoButton,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		spaceKey,
		aris,
		initialised,
		invokeRuleOrShowDialog,
		invokingRuleId,
		rules,
		error,
		baseAutomationUrl,
		analyticsSource,
	} = useAutomationMenu();

	const { canManageAutomations, error: permissionServiceError } =
		useAutomationPermissionService(spaceKey);
	const experienceTracker = useContext(ExperienceTrackerContext);

	// Just to signal undesired footer results due to permission service failing.
	useEffect(() => {
		if (permissionServiceError) {
			experienceTracker.fail({
				name: AUTOMATION_MANUAL_TRIGGERS,
				error: permissionServiceError,
				attributes: {
					source: analyticsSource,
					location: 'footer',
				},
			});
		}
	}, [experienceTracker, analyticsSource, permissionServiceError]);

	const sendFooterAnalyticsEvent = (actionSubjectId: string, ruleId?: string) => {
		const event = createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId,
				source: analyticsSource,
			},
		});
		if (ruleId) {
			event.payload.data.attributes = { ruleId };
		}
		event.fire();
	};

	const okButton = (
		<Button
			key="automation-modal-footer-ok"
			appearance="primary"
			onClick={() => {
				sendFooterAnalyticsEvent('ok');
				onClose();
			}}
		>
			<FormattedMessage {...i18n.modalFooterOkButton} />
		</Button>
	);

	const cancelButton = (
		<Button
			key="automation-modal-footer-cancel"
			appearance="subtle"
			onClick={() => {
				sendFooterAnalyticsEvent('cancel');
				onClose();
			}}
		>
			<FormattedMessage {...i18n.modalFooterCancelButton} />
		</Button>
	);

	const createAutomationButton = (
		<LinkButton
			key="automation-modal-footer-create-automation"
			appearance="primary"
			href={`${baseAutomationUrl}#rule/new`}
			onClick={() => {
				sendFooterAnalyticsEvent('createAutomation');
			}}
			target="_blank"
		>
			<FormattedMessage {...i18n.modalFooterCreateAutomationButton} />
		</LinkButton>
	);

	const automateButton = (
		<Button
			key="automation-modal-footer-automate"
			appearance="primary"
			onClick={() => {
				if (selectedRuleId) {
					sendFooterAnalyticsEvent('executeManualTriggerAutomation', selectedRuleId.toString());
					invokeRuleOrShowDialog(selectedRuleId, aris.map(String));
				}
			}}
			isDisabled={!selectedRuleId || !!invokingRuleId}
		>
			<FormattedMessage {...i18n.modalFooterAutomateButton} />
		</Button>
	);

	const modalButtons: JSX.Element[] = [];

	if (!initialised) return null;

	// Populated rule list -> cancel and automate buttons
	// Empty admin scenario -> cancel and Create Automation buttons
	// Empty end user and error scenario -> Ok button
	if (rules.length < 1) {
		if (error || !canManageAutomations) {
			modalButtons.push(okButton);
		} else {
			modalButtons.push(cancelButton, createAutomationButton);
		}
	} else {
		modalButtons.push(cancelButton, automateButton);
	}

	// Don't show cancel and Ok button in Rovo Button experience
	if (inRovoButton) {
		const okIndex = modalButtons.indexOf(okButton);
		const cancelIndex = modalButtons.indexOf(cancelButton);
		if (okIndex > -1) {
			modalButtons.splice(okIndex, 1);
		}

		if (cancelIndex > -1) {
			modalButtons.splice(cancelIndex, 1);
		}
	}

	return <>{modalButtons}</>;
};
